<script lang="ts">
    import { DEFAULT_BOX, DEFAULT_LAYOUT } from '$lib/components/constants';

    import { cn } from '$lib/utils/cn';

    import ImgixImage from './ImgixImage.svelte';
    import {
        maxWidth,
        maxWidthDesktop,
        maxWidthAnswer,
        maxWidthDesktopAnswer,
        desktopBreakpoint,
        imgixAr,
    } from '../_utils/constants';
    import { getBoxAttributes } from '../_utils/getBoxAttributes';
    import { getParamsFromUrl, getRatioBySize, stripCropParams } from '../_utils/helpers';
    import Box from '../_wrappers/Box.svelte';

    import type { Box as BoxType } from '$lib/types/Components/SharedTypes';
    import type { ImageSize } from '$lib/types/Components/SharedTypes';
    import type { TransitionEffect } from '$lib/types/Theme';

    export let id = '';
    export let src = '';
    export let size: ImageSize = 'medium';
    export let framed = false;
    export let transitionDelay = 0;
    export let transitionEffect: TransitionEffect = 'default';
    export let fixedMaxWidth: number;
    export let borderRadiusClass = '';
    export let shadowClass: string | undefined = undefined;
    export let layout = DEFAULT_LAYOUT;
    export let onClick: (() => void) | undefined = undefined;
    export let alt = '';
    export let box: BoxType = DEFAULT_BOX;
    export let ariaLabel = '';

    const existingParams = getParamsFromUrl(src) ?? {};

    const imgixParams: { w?: number; h?: number; ar?: number | string; [key: string]: unknown } = {
        ...existingParams,
        q: 80,
        fit: 'crop',
        auto: ['format', 'compress'],
        ...(size !== 'custom' ? { ar: imgixAr[size] } : {}),
        ...(fixedMaxWidth ? { w: fixedMaxWidth } : {}),
    };

    // For custom sizes, proportionally adjust h to maintain the original aspect ratio
    if (size === 'custom' && fixedMaxWidth && existingParams.w && existingParams.h) {
        const originalW = parseFloat(existingParams.w as string);
        const originalH = parseFloat(existingParams.h as string);

        if (originalW > 0 && originalH > 0) {
            imgixParams.h = Math.round(fixedMaxWidth * (originalH / originalW));
        }
    }

    const getSizes = (size: ImageSize) => {
        let mobileWidth = maxWidth;
        let desktopWidth = fixedMaxWidth || maxWidthDesktop;

        if (size === 'answer') {
            mobileWidth = maxWidthAnswer;
            desktopWidth = maxWidthDesktopAnswer;
        }

        if (fixedMaxWidth && size === 'listItem') {
            mobileWidth = fixedMaxWidth;
            desktopWidth = fixedMaxWidth;
        }

        return `(max-width: ${desktopBreakpoint - 1}px) ${mobileWidth}px, ${desktopWidth}px`;
    };

    const { isInFramedColumn, isInColumn } = layout;

    const sizes = getSizes(size);
    const ratio = getRatioBySize({ height: imgixParams.h, width: imgixParams.w, size });
    const imageWrapperClass = cn(
        'fun-overflow-hidden fun-leading-[0]',
        borderRadiusClass,
        shadowClass,
        {
            ['fun-w-[calc(100%+48px)] fun--translate-x-6 sm:fun-w-full sm:fun-translate-x-0']:
                !isInFramedColumn && isInColumn,
            ['fun-cursor-pointer']: !!onClick,
        },
    );

    const handleKeyDown = (event: KeyboardEvent) => {
        if (onClick && (event.key === ' ' || event.key === 'Enter')) {
            event.preventDefault();
            event.stopPropagation();
            onClick();
        }
    };

    const adjustedSrc = src.indexOf('.gif') > -1 ? stripCropParams(src) : src;
</script>

<Box
    {id}
    {...getBoxAttributes({ box, isInColumn })}
    medium={framed}
    {transitionEffect}
    {transitionDelay}
>
    <div
        class={framed && !isInColumn
            ? 'sm:fun-px-12 sm:fun-py-6 md:fun-px-20 md:fun-py-10'
            : 'fun-p-0'}
    >
        <div
            class={imageWrapperClass}
            on:click={onClick}
            on:keydown={onClick ? handleKeyDown : undefined}
            tabindex={onClick ? 0 : undefined}
            role={onClick ? 'button' : undefined}
            aria-label={ariaLabel}
        >
            <ImgixImage
                src={adjustedSrc}
                ratio={ratio ? `${ratio}%` : null}
                {imgixParams}
                {sizes}
                {alt}
            />
        </div>
    </div>
</Box>
